import React, { useEffect, useState } from "react";
import {
   Box,
   Button,
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableHead,
   TableRow,
   TextField,
   Typography,
   Paper,
   IconButton,
   Popover,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Collapse,
   Divider,
   useMediaQuery,
   useTheme,
} from "@mui/material";
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
   getItemType,
   createItemType,
   updateItemType,
   deleteItemType,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Itemtypelist = () => {
   const [units, setUnits] = useState([]);
   const [open, setOpen] = useState(false);

   // ✅ Responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const [expandedUnit, setExpandedUnit] = useState(null);

   // For Add Mode → multiple rows
   const [unitForms, setUnitForms] = useState([{ ItemType: "", symbol: "" }]);
   const [touched, setTouched] = useState([{ ItemType: false, symbol: false }]);

   // For Edit Mode → single row
   const [unitForm, setUnitForm] = useState({
      ItemType: "",
      created_by: localStorage.getItem("user_id"),
   });

   const [editId, setEditId] = useState(null);

   // Popover state for delete confirmation
   const [anchorEl, setAnchorEl] = useState(null);
   const [deleteId, setDeleteId] = useState(null);

   const [searchTerm, setSearchTerm] = useState(""); // search state

   const userId = localStorage.getItem("user_id");

   // Fetch all units
   const fetchUnits = async () => {
      const data = await getItemType();
      setUnits(data);
   };

   useEffect(() => {
      fetchUnits();
   }, []);

   // Add new row in Add Mode
   const handleAddRow = () => {
      if (unitForms.length >= 5) {
         toast.error("You can add maximum 5 units");
         return;
      }
      setUnitForms([...unitForms, { ItemType: ""}]);
      setTouched([...touched, { ItemType: false }]);
   };

   const handleRemoveField = (index) => {
      const updatedForms = unitForms.filter((_, i) => i !== index);
      setUnitForms(updatedForms);

      const updatedTouched = touched.filter((_, i) => i !== index);
      setTouched(updatedTouched);
   };

   // Change value in Add Mode
   const handleChange = (index, field, value) => {
      const updatedForms = [...unitForms];
      updatedForms[index][field] = value;
      setUnitForms(updatedForms);
   };

   // Blur validation in Add Mode
   const handleBlur = (index, field) => {
      const updatedTouched = [...touched];
      updatedTouched[index][field] = true;
      setTouched(updatedTouched);
   };

   // ✅ Save
   const handleSave = async () => {
      if (editId) {
         // ---- Edit Mode ----
         if (!unitForm.ItemType.trim()) {
            toast.error("ItemType is required!");
            return;
         }
         try {
            await updateItemType(editId, unitForm);
            toast.success("Unit updated successfully!");
            fetchUnits();
            setOpen(false);
         } catch (error) {
            toast.error("Something went wrong!");
         }
      } else {
         // ---- Add Mode ----
         const allTouched = unitForms.map(() => ({ ItemType: true }));
         setTouched(allTouched);

         const isValid = unitForms.every(
            (form) => form.ItemType.trim() !== "" 
         );
         if (!isValid) {
            toast.error("Please fill all required fields!");
            return;
         }

         try {
            for (let form of unitForms) {
               await createItemType({
                  ...form,
                  created_by: userId,
               });
            }
            toast.success("Item Type added successfully!");
            fetchUnits();
            setUnitForms([{ ItemType: "", symbol: "" }]);
            setTouched([{ ItemType: false }]);
            setOpen(false);
         } catch (error) {
            toast.error("Something went wrong!");
         }
      }
   };

   // Edit
   const handleEdit = (unit) => {
      setUnitForm({
         ItemType: unit.ItemType || "",
        
         created_by: userId,
      });
      setEditId(unit.id);
      setOpen(true);
   };

   // Delete
   const handleDeleteClick = (event, id) => {
      setAnchorEl(event.currentTarget);
      setDeleteId(id);
   };

   const handlePopoverClose = () => {
      setAnchorEl(null);
      setDeleteId(null);
   };

   const confirmDelete = async () => {
      if (deleteId) {
         await deleteItemType(deleteId);
         setUnits((prev) => prev.filter((unit) => unit.id !== deleteId));
         handlePopoverClose();
         toast.success("Unit deleted successfully!");
      }
   };

   const openPopover = Boolean(anchorEl);

   // Filtered Units
   const filteredUnits = units.filter(
      (unit) =>
         unit.ItemType.toLowerCase().includes(searchTerm.toLowerCase())
    
   );



   useEffect(() => {
      document.body.style.overflow = isMobile ? 'auto' : 'hidden';

      return () => {
         document.body.style.overflow = 'auto';
      };
   }, [isMobile]);
   return (
      <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1200, mx: "auto" }}>
         <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

         {/* Top bar with Search + Add */}
         <Box
            sx={{
               display: "flex",
               flexDirection: { xs: "column", sm: "row" },
                 justifyContent: "flex-end" ,
               gap: 1,
               mb: 1,
               alignItems: { xs: "stretch", sm: "center" },
            }}
         >

            <Button
               variant="contained"
               size="small"
               startIcon={<AddIcon sx={{ fontSize: 16 }} />}
               onClick={() => {
                  setOpen(true);
                  setEditId(null);
                  setUnitForms([{ ItemType: "", symbol: "" }]);
                  setTouched([{ ItemType: false, symbol: false }]);
               }}
               sx={{
                  height: 30,
                  fontSize: "0.7rem",
                  padding: "2px 12px",
                  borderRadius: "0px",
                  textTransform: "none",
               }}
            >
               Item Type
            </Button>
         </Box>

         {/* 🔥 CONDITIONAL RENDERING - Mobile vs Desktop */}
         {isMobile ? (
            /* 📱 MOBILE VIEW - Expandable List */
            <Box sx={{ bgcolor: "background.paper" }}>
               {filteredUnits.length > 0 ? (
                  filteredUnits.map((unit, index) => (
                     <Box key={unit.id}>
                        {/* Main Row - Always Visible */}
                        <Box
                           onClick={() =>
                              setExpandedUnit(expandedUnit === unit.id ? null : unit.id)
                           }
                           sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              p: 1.5,
                              cursor: "pointer",
                              bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                              "&:active": {
                                 bgcolor: "#e3f2fd",
                              },
                              borderLeft: "4px solid #1976d2",
                           }}
                        >
                           {/* Left Side - Main Info */}
                           <Box sx={{ flex: 1 }}>
                              {/* S.No + Unit Name */}
                              <Box
                                 sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 0.5,
                                 }}
                              >
                                 <Typography
                                    sx={{
                                       fontSize: "0.7rem",
                                       color: "#666",
                                       minWidth: 25,
                                    }}
                                 >
                                    #{index + 1}
                                 </Typography>
                                 <Typography
                                    sx={{
                                       fontWeight: "bold",
                                       fontSize: "0.85rem",
                                       flex: 1,
                                    }}
                                 >
                                    {unit.ItemType}
                                 </Typography>
                              </Box>

                              {/* Symbol */}
                              <Typography
                                 sx={{
                                    fontSize: "0.75rem",
                                    color: "#666",
                                 }}
                              >
                                 Symbol: {unit.symbol}
                              </Typography>
                           </Box>

                           {/* Expand Icon */}
                           <IconButton size="small" sx={{ ml: 1 }}>
                              {expandedUnit === unit.id ? (
                                 <ExpandLess fontSize="small" />
                              ) : (
                                 <ExpandMore fontSize="small" />
                              )}
                           </IconButton>
                        </Box>

                        {/* Expanded Details */}
                        <Collapse
                           in={expandedUnit === unit.id}
                           timeout="auto"
                           unmountOnExit
                        >
                           <Box
                              sx={{
                                 px: 1.5,
                                 py: 1,
                                 bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                                 borderLeft: "4px solid #1976d2",
                              }}
                           >
                              {/* Detailed Info - 2 Column Grid */}
                              <Box
                                 sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 0.8,
                                    mb: 1,
                                 }}
                              >
                                 {/* S.NO */}
                                 <Box>
                                    <Typography
                                       sx={{
                                          fontSize: "0.6rem",
                                          color: "#999",
                                          letterSpacing: "0.5px",
                                       }}
                                    >
                                       S.NO
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontSize: "0.72rem",
                                          fontWeight: 500,
                                          color: "#555",
                                       }}
                                    >
                                       #{index + 1}
                                    </Typography>
                                 </Box>

                                 {/* SYMBOL */}
                                 <Box>
                                    <Typography
                                       sx={{
                                          fontSize: "0.6rem",
                                          color: "#999",
                                          letterSpacing: "0.5px",
                                       }}
                                    >
                                       SYMBOL
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontSize: "0.72rem",
                                          fontWeight: 500,
                                          color: "#555",
                                       }}
                                    >
                                       {unit.symbol}
                                    </Typography>
                                 </Box>
                              </Box>

                              {/* Unit Name - Full Width */}
                              <Box sx={{ mb: 0.8 }}>
                                 <Typography
                                    sx={{
                                       fontSize: "0.6rem",
                                       color: "#999",
                                       letterSpacing: "0.5px",
                                       mb: 0.3,
                                    }}
                                 >
                                    Item Type
                                 </Typography>
                                 <Typography
                                    sx={{
                                       fontSize: "0.7rem",
                                       color: "#555",
                                       lineHeight: 1.3,
                                       fontWeight: 500,
                                    }}
                                 >
                                    {unit.ItemType}
                                 </Typography>
                              </Box>

                              <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                              {/* Action Buttons */}
                              <Box
                                 sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 1,
                                 }}
                              >
                                 <Typography
                                    sx={{
                                       fontSize: "0.6rem",
                                       color: "#999",
                                       fontWeight: 600,
                                       letterSpacing: "0.5px",
                                    }}
                                 >
                                    ACTIONS
                                 </Typography>

                                 {/* Edit & Delete Buttons */}
                                 <Box sx={{ display: "flex", gap: 0.6 }}>
                                    <IconButton
                                       size="small"
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          handleEdit(unit);
                                       }}
                                       sx={{
                                          bgcolor: "#e3f2fd",
                                          borderRadius: "6px",
                                          width: 32,
                                          height: 32,
                                          "&:hover": {
                                             bgcolor: "#bbdefb",
                                             transform: "scale(1.05)",
                                          },
                                          transition: "all 0.2s",
                                       }}
                                    >
                                       <EditIcon
                                          sx={{ fontSize: "1rem", color: "#1976d2" }}
                                       />
                                    </IconButton>

                                    <IconButton
                                       size="small"
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteClick(e, unit.id);
                                       }}
                                       sx={{
                                          bgcolor: "#ffebee",
                                          borderRadius: "6px",
                                          width: 32,
                                          height: 32,
                                          "&:hover": {
                                             bgcolor: "#ffcdd2",
                                             transform: "scale(1.05)",
                                          },
                                          transition: "all 0.2s",
                                       }}
                                    >
                                       <DeleteIcon
                                          sx={{ fontSize: "1rem", color: "#d32f2f" }}
                                       />
                                    </IconButton>
                                 </Box>
                              </Box>
                           </Box>
                        </Collapse>

                        {/* Divider between items */}
                        <Divider />
                     </Box>
                  ))
               ) : (
                  <Box sx={{ textAlign: "center", py: 4, color: "#999" }}>
                     <Typography>No matching records found</Typography>
                  </Box>
               )}
            </Box>
         ) : (
            /* 💻 DESKTOP VIEW - Table */
            <TableContainer
               component={Paper}
               sx={{
                  maxHeight: "88vh",
                  overflowX: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
               }}
            >
               <Table
                  size="small"
                  stickyHeader
                  sx={{
                     minWidth: 1000,
                     "& td, & th": {
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        borderBottom: "1px solid #ccc",
                     },
                  }}
               >
                  <TableHead>
                     <TableRow
                        sx={{
                           backgroundColor: "#f5f5f5",
                           "& th": {
                              fontWeight: "bold",
                              color: "#333",
                              borderBottom: "1px solid #999",
                              backgroundColor: "#f5f5f5",
                              fontsize: "0.75rem",
                           },
                        }}
                     >
                        <TableCell>S.No</TableCell>
                        <TableCell>Item Type</TableCell>
                        {/* <TableCell>Symbol</TableCell> */}
                        <TableCell>Actions</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {filteredUnits.length > 0 ? (
                        filteredUnits.map((unit, index) => (
                           <TableRow
                              hover
                              key={unit.id}
                              sx={{
                                 backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                                 "& td": {
                                    paddingRight: "13px !important",
                                    paddingLeft: "13px !important",
                                    padding: "0px",
                                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                    whiteSpace: "nowrap",
                                 },
                                 "&:hover": { backgroundColor: "#e3f2fd !important" },
                              }}
                           >
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{unit.ItemType}</TableCell>
                              {/* <TableCell>{unit.symbol}</TableCell> */}
                              <TableCell>
                                 <IconButton
                                    sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEdit(unit)}
                                 >
                                    <EditIcon transform="scale(0.6)" />
                                 </IconButton>
                                 <IconButton
                                    sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                                    size="small"
                                    color="error"
                                    onClick={(e) => handleDeleteClick(e, unit.id)}
                                 >
                                    <DeleteIcon transform="scale(0.6)" />
                                 </IconButton>
                              </TableCell>
                           </TableRow>
                        ))
                     ) : (
                        <TableRow>
                           <TableCell colSpan={4} align="center">
                              No matching records found
                           </TableCell>
                        </TableRow>
                     )}
                  </TableBody>
               </Table>
            </TableContainer>
         )}

         {/* Add/Edit Modal */}
         <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>{editId ? "Edit Item Type" : "Add Item Type"}</DialogTitle>
            <DialogContent dividers>
               {editId ? (
                  // ---- Edit Mode: Single row ----
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                     <TextField
                        label="Item Type"
                        fullWidth
                        value={unitForm.ItemType}
                        onChange={(e) =>
                           setUnitForm({ ...unitForm, ItemType: e.target.value })
                        }
                     />
                     {/* <TextField
                        label="Symbol"
                        fullWidth
                        value={unitForm.symbol}
                        onChange={(e) =>
                           setUnitForm({ ...unitForm, symbol: e.target.value })
                        }
                     /> */}
                  </Box>
               ) : (
                  // ---- Add Mode: Multiple rows ----
                  <Box>
                     {unitForms.map((form, index) => (
                        <Box key={index} sx={{ display: "flex", gap: 2, mb: 2 }}>
                           <TextField
                              label="Item Type"
                              fullWidth
                              value={form.ItemType}
                              onChange={(e) => handleChange(index, "ItemType", e.target.value)}
                              onBlur={() => handleBlur(index, "ItemType")}
                              error={touched[index]?.ItemType && !form.ItemType}
                              helperText={
                                 touched[index]?.ItemType && !form.ItemType
                                    ? "Item Type is required"
                                    : ""
                              }
                           />
                           {/* <TextField
                              label="Symbol"
                              fullWidth
                              value={form.symbol}
                              onChange={(e) =>
                                 handleChange(index, "symbol", e.target.value)
                              }
                              onBlur={() => handleBlur(index, "symbol")}
                              error={touched[index]?.symbol && !form.symbol}
                              helperText={
                                 touched[index]?.symbol && !form.symbol
                                    ? "Symbol is required"
                                    : ""
                              }
                           /> */}
                           {unitForms.length > 1 && (
                              <IconButton
                                 color="error"
                                 size="small"
                                 onClick={() => handleRemoveField(index)}
                              >
                                 <DeleteIcon fontSize="small" />
                              </IconButton>
                           )}
                        </Box>
                     ))}
                  </Box>
               )}
            </DialogContent>

            {!editId && (
               <DialogActions
                  sx={{
                     justifyContent: "flex-start",
                     mb: -5,
                  }}
               >
                  <Button
                     type="button"
                     variant="outlined"
                     startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                     onClick={handleAddRow}
                     sx={{
                        height: 35,
                        fontSize: 14,
                        padding: "2px 10px",
                        minWidth: "auto",
                        textTransform: "none",
                        border: "none",
                        display: "flex",
                        alignSelf: "flex-start",
                     }}
                  >
                     ADD ROW
                  </Button>
               </DialogActions>
            )}

            <DialogActions>
               <Button color="error" onClick={() => setOpen(false)}>
                  CLOSE
               </Button>
               <Button onClick={handleSave}>{editId ? "Update" : "Save"}</Button>
            </DialogActions>
         </Dialog>

         {/* Delete Confirmation Popover */}
         <Popover
            open={openPopover}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
         >
            <Box sx={{ p: 2, maxWidth: 220 }}>
               <Typography>Are you sure ?</Typography>
               <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  <Button color="error" size="small" onClick={confirmDelete}>
                     Yes
                  </Button>
                  <Button size="small" onClick={handlePopoverClose}>
                     No
                  </Button>
               </Box>
            </Box>
         </Popover>
      </Box>
   );
};

export default Itemtypelist;